import React from 'react';

import {
	FormControl,
	type FormControlProps,
	FormControlLabel,
	FormLabel,
	RadioGroup,
} from '@mui/material';
import {useEvent} from '@netx/core-components/hooks/useEvent';
import {ErrorField} from '@netx/core-components/ErrorField';

export interface RadiosFieldProps
	extends Omit<FormControlProps<'fieldset'>, 'error' | 'form' | 'onChange'> {
	children: React.ReactElement<typeof FormControlLabel>[];
	label: string;
	error?: string;
	hideErrorMessage?: boolean;
	readOnly?: boolean;
	value?: string | null;
	onChange?: (
		event: React.ChangeEvent<HTMLInputElement>,
		value: string,
	) => void;
}

export const RadiosFieldPrimitive = ({
	children,
	disabled,
	error,
	hideErrorMessage,
	hiddenLabel,
	label,
	name,
	readOnly,
	required,
	value,
	onChange,
	...props
}: RadiosFieldProps) => {
	const handleChange = useEvent(
		(event: React.ChangeEvent<HTMLInputElement>, value: string) => {
			if (!event) return;
			onChange?.(event, value);
		},
	);

	return (
		<FormControl
			component="fieldset"
			disabled={disabled}
			required={required}
			{...props}
		>
			<FormLabel id={`${name}-label`}>{label}</FormLabel>
			<RadioGroup
				row
				aria-labelledby={`${name}-label`}
				name={name}
				value={value}
				onChange={handleChange}
			>
				{children}
			</RadioGroup>

			{!hideErrorMessage ? (
				<ErrorField disabled={disabled} error={error} />
			) : null}
		</FormControl>
	);
};

/**
 * Wrapper for `FormControlLabel` to simplify creating a group of radio buttons for consumption.
 */
export const RadiosField = RadiosFieldPrimitive;

export default RadiosField;
